import { format, parseISO } from 'date-fns';
import { parseZonedTimeToUtc } from 'src/utilities/parseZonedTimeToUtc';
import { IInvoice } from 'src/utilities/useSearchHooks';
import { sdk } from 'src/utilities/sdk';
import { MediaTypes } from 'src/utilities/saveFile';

export enum InvoiceState {
  UNPAID = 'UNPAID',
  CC_PAYMENT_FAILED = 'CC_PAYMENT_FAILED',
  CC_PAYMENT_SUCCESS = 'CC_PAYMENT_SUCCESS',
  PAID = 'PAID',
  CC_PAYMENT_SCHEDULED = 'CC_PAYMENT_SCHEDULED',
  CC_PAYMENT_PENDING = 'CC_PAYMENT_PENDING',
  CC_OVERPAYMENT = 'CC_OVERPAYMENT',
}

export class TransactionFormat {
  public static CORP_ADMIN = 'CORP_ADMIN';
  public static ORIGINAL = 'ORIGINAL';
}
export function getTransactionsCSVFileName(invoice: IInvoice): string {
  const start = format(parseISO(invoice.start), 'yyyy MM dd');
  const end = format(parseISO(invoice.end), 'yyyy MM dd');
  const sYear = start.substr(0, 4);
  const sMonth = start.substr(5, 2);
  const sDay = start.substr(8, 2);
  const eYear = end.substr(0, 4);
  const eMonth = end.substr(5, 2);
  const eDay = end.substr(8, 2);

  return `invoice_data_${sYear}${sMonth}${sDay}-${eYear}${eMonth}${eDay}.csv`;
}

export function getInvoiceFileName(
  documentType: MediaTypes,
  invoiceNumber: string
): string {
  const fileName = `invoice_${invoiceNumber}`;
  if (documentType === MediaTypes.TEXT_CSV) {
    return `${fileName}.csv`;
  }

  if (documentType === MediaTypes.APPLICATION_PDF) {
    return `${fileName}.pdf`;
  }
  return '';
}

export async function getInvoiceTransactionsFallback(
  downloadEndPoint: string,
  isArrayBuffer = false
): Promise<string> {
  let configObject;

  if (isArrayBuffer) {
    configObject = {
      responseType: 'arraybuffer',
    };
  } else {
    configObject = {
      headers: {
        Accept: MediaTypes.TEXT_CSV,
      },
    };
  }

  const response = await sdk.http.get(downloadEndPoint, configObject);
  return response.data;
}

const SHORT_DATE_FORMAT = 'MM/dd/yy';

export function invoiceDueDateFormat(value: string | Array<string>): string {
  if (Array.isArray(value)) {
    const [year, month, day] = value;
    return format(parseISO(`${year}-${month}-${day}`), SHORT_DATE_FORMAT);
  }
  return format(parseISO(value), SHORT_DATE_FORMAT);
}

export function toUTCDateFormat(date: string): string {
  if (!date) {
    return '';
  }
  const utcDate = parseZonedTimeToUtc(date, 'UTC');
  if (utcDate === null) {
    return '';
  }
  return format(utcDate, SHORT_DATE_FORMAT);
}

export function getAmountOwed(
  amountOwed: number,
  invoiceState: InvoiceState
): number {
  switch (invoiceState) {
    case InvoiceState.CC_OVERPAYMENT:
    case InvoiceState.CC_PAYMENT_SUCCESS:
    case InvoiceState.PAID:
      return 0;
    default:
      return amountOwed;
  }
}

export function getStatusMessage(
  invoice: IInvoice,
  renderMessage: (o: { message: string; color?: string }) => JSX.Element
): JSX.Element {
  switch (invoice.state) {
    case InvoiceState.CC_PAYMENT_SUCCESS:
    case InvoiceState.PAID:
      return renderMessage({
        message: invoice.cc_payment_date
          ? `Paid ${toUTCDateFormat(invoice.cc_payment_date)}`
          : 'Paid',
        color: 'success',
      });
    case InvoiceState.CC_PAYMENT_SCHEDULED:
      return renderMessage({
        message: 'Scheduled',
      });
    case InvoiceState.CC_PAYMENT_PENDING:
      return renderMessage({
        message: 'Processing',
      });
    case InvoiceState.CC_OVERPAYMENT:
      return renderMessage({
        message: 'Paid',
        color: 'success',
      });
    default:
      return renderMessage({ message: '' });
  }
}
